'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowDown, RefreshCw, Check } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { invalidateCrmData } from '@/lib/live-sync';
import { cn } from '@/lib/utils';

export interface PullToRefreshProps {
  children: React.ReactNode;
  /**
   * Reference to the scrollable container. If omitted, listeners attach to this component's DOM container.
   */
  containerRef?: React.RefObject<HTMLElement | null>;
  /**
   * Custom refresh callback. If omitted, default invalidates all CRM scopes,
   * refetches all active React Query queries, and calls router.refresh().
   */
  onRefresh?: () => Promise<void> | void;
  /**
   * Distance in pixels required to trigger a refresh (default: 56px).
   */
  threshold?: number;
  /**
   * Maximum pull distance allowed (default: 84px).
   */
  maxPull?: number;
  /**
   * Whether pull-to-refresh is disabled.
   */
  disabled?: boolean;
  className?: string;
}

export function PullToRefresh({
  children,
  containerRef,
  onRefresh,
  threshold = 56,
  maxPull = 84,
  disabled = false,
  className,
}: PullToRefreshProps) {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const fallbackRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef(0);
  const startXRef = useRef(0);
  const pullingRef = useRef(false);
  const isRefreshingRef = useRef(false);
  const readyRef = useRef(false);
  const pullDistanceRef = useRef(0);

  const queryClient = useQueryClient();
  const router = useRouter();

  // Keep refs in sync for event listeners attached once
  isRefreshingRef.current = isRefreshing;
  pullDistanceRef.current = pullDistance;

  const triggerHaptic = useCallback((pattern: number | number[]) => {
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try {
        navigator.vibrate(pattern);
      } catch {
        // Ignore on unsupported browsers
      }
    }
  }, []);

  const executeRefresh = useCallback(async () => {
    if (onRefresh) {
      await onRefresh();
      return;
    }

    // Default CRM refresh:
    // 1. Notify all live-sync hooks across the app
    invalidateCrmData('all');

    // 2. Refetch all active queries currently rendered
    try {
      await queryClient.refetchQueries({ type: 'active' });
    } catch (e) {
      console.warn('PullToRefresh query refetch error:', e);
    }

    // 3. Refresh Next.js route components
    try {
      router.refresh();
    } catch {
      // Ignore router errors
    }
  }, [onRefresh, queryClient, router]);

  useEffect(() => {
    const targetElement = containerRef?.current || fallbackRef.current?.parentElement || fallbackRef.current;
    if (!targetElement) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (disabled || isRefreshingRef.current) return;

      // Only initiate pull-to-refresh if container is at the very top
      const scrollTop = targetElement.scrollTop ?? 0;
      if (scrollTop > 2) {
        pullingRef.current = false;
        return;
      }

      const touch = e.touches[0];
      startYRef.current = touch.clientY;
      startXRef.current = touch.clientX;
      pullingRef.current = false;
      readyRef.current = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (disabled || isRefreshingRef.current) return;

      const scrollTop = targetElement.scrollTop ?? 0;
      if (scrollTop > 2) {
        if (pullingRef.current) {
          pullingRef.current = false;
          setPullDistance(0);
          setIsReady(false);
        }
        return;
      }

      const touch = e.touches[0];
      const dy = touch.clientY - startYRef.current;
      const dx = touch.clientX - startXRef.current;

      // If horizontal swipe is dominant (e.g., table scroll or tab swipe), ignore
      if (Math.abs(dx) > Math.abs(dy)) {
        return;
      }

      // Dragging downwards while at top
      if (dy > 0 && scrollTop <= 2) {
        pullingRef.current = true;

        // Prevent native rubber-band clipping when actively pulling down
        if (e.cancelable) {
          e.preventDefault();
        }

        // Apply smooth elastic resistance formula
        const elasticDistance = Math.min(maxPull, Math.pow(dy, 0.76) * 2.3);
        setPullDistance(elasticDistance);

        const meetsThreshold = elasticDistance >= threshold;
        if (meetsThreshold !== readyRef.current) {
          readyRef.current = meetsThreshold;
          setIsReady(meetsThreshold);
          if (meetsThreshold) {
            triggerHaptic(12);
          }
        }
      }
    };

    const handleTouchEnd = async () => {
      if (!pullingRef.current || disabled || isRefreshingRef.current) {
        pullingRef.current = false;
        return;
      }

      pullingRef.current = false;
      const shouldRefresh = pullDistanceRef.current >= threshold;

      if (shouldRefresh) {
        setIsRefreshing(true);
        isRefreshingRef.current = true;
        setPullDistance(48); // Snap to comfortable holding height

        const startTime = Date.now();
        try {
          await executeRefresh();
        } catch (err) {
          console.error('PullToRefresh execution failed:', err);
        } finally {
          // Keep spinner visible for at least 650ms for smooth feedback
          const elapsed = Date.now() - startTime;
          const remaining = Math.max(0, 650 - elapsed);

          setTimeout(() => {
            setIsSuccess(true);
            triggerHaptic([12, 40, 12]);

            setTimeout(() => {
              setIsRefreshing(false);
              isRefreshingRef.current = false;
              setIsSuccess(false);
              setIsReady(false);
              setPullDistance(0);
              readyRef.current = false;
            }, 350);
          }, remaining);
        }
      } else {
        // Snaps smoothly back to 0
        setPullDistance(0);
        setIsReady(false);
        readyRef.current = false;
      }
    };

    targetElement.addEventListener('touchstart', handleTouchStart, { passive: true });
    targetElement.addEventListener('touchmove', handleTouchMove, { passive: false });
    targetElement.addEventListener('touchend', handleTouchEnd, { passive: true });
    targetElement.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      targetElement.removeEventListener('touchstart', handleTouchStart);
      targetElement.removeEventListener('touchmove', handleTouchMove);
      targetElement.removeEventListener('touchend', handleTouchEnd);
      targetElement.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [containerRef, disabled, threshold, maxPull, executeRefresh, triggerHaptic]);

  const isVisible = pullDistance > 0 || isRefreshing;

  return (
    <div ref={fallbackRef} className={cn('relative w-full', className)}>
      {/* Pull down indicator pill */}
      <div
        className="w-full flex items-center justify-center overflow-hidden transition-[height,opacity] pointer-events-none select-none z-30"
        style={{
          height: `${pullDistance}px`,
          transition: pullingRef.current
            ? 'none'
            : 'height 0.28s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.2s ease',
          opacity: isRefreshing || isSuccess
            ? 1
            : Math.min(1, Math.max(0, (pullDistance - 12) / (threshold - 12))),
        }}
      >
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border border-emerald-500/30 dark:border-emerald-500/40 shadow-lg shadow-emerald-500/10 text-xs font-semibold text-slate-800 dark:text-slate-100 mb-1">
          {isRefreshing ? (
            <>
              <RefreshCw className="h-3.5 w-3.5 text-emerald-500 animate-spin" />
              <span>Updating live data...</span>
            </>
          ) : isSuccess ? (
            <>
              <Check className="h-3.5 w-3.5 text-emerald-500" />
              <span className="text-emerald-600 dark:text-emerald-400">Updated</span>
            </>
          ) : (
            <>
              <ArrowDown
                className="h-3.5 w-3.5 text-emerald-500 transition-transform duration-200"
                style={{ transform: `rotate(${isReady ? 180 : 0}deg)` }}
              />
              <span>{isReady ? 'Release to refresh' : 'Pull down to refresh'}</span>
            </>
          )}
        </div>
      </div>

      {/* Page Content */}
      {children}
    </div>
  );
}
